import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import Icon from "../../Icon";
import AmPmSelector from "./internal/AmPmSelector";
import HourSelector from "./internal/HourSelector";
import MinuteSelector from "./internal/MinuteSelector";

interface TimeSelectorProps {
  className?: string; // Applied to all.
  classes?: {
    container?: string;
    hour?: string;
    icon?: string;
    colon?: string;
    minute?: string;
    ampm?: string;
  };
  disabled?: boolean;
  hideIcon?: boolean;
  loading?: boolean;
  onChange: (time: string | null) => void; // time is 24-hour time
  onBlur?: () => void;
  timeFormat: string;
  value?: string | null; // 24-hour time
}

export function TimeSelector({
  className = "",
  classes = {},
  disabled = false,
  hideIcon = false,
  loading = false,
  onChange,
  onBlur,
  timeFormat,
  value,
}: TimeSelectorProps) {
  const [hour, setHour] = useState<number>();
  const [minute, setMinute] = useState<number>();
  const [amPm, setAmPm] = useState<string>();
  const militaryTime = timeFormat == "twentyfour";

  // If the value prop changes update our internal state (also runs on mount).
  useEffect(() => {
    let parsedTime, newAmPm, newHour, newMinute;
    if (value) {
      try {
        parsedTime = parse(value, "H:mm", new Date());
        newHour = militaryTime
          ? parsedTime.getHours()
          : parseInt(format(parsedTime, "h"));
        newMinute = parsedTime.getMinutes();
        newAmPm = format(parsedTime, "aaa");
      } catch (error) {
        console.error(error);
      }
    }
    setHour(newHour);
    setMinute(newMinute);
    setAmPm(newAmPm);
  }, [militaryTime, value]);

  // Execute onChange if hour/minute change.
  useEffect(() => {
    if (
      hour == undefined ||
      isNaN(hour) ||
      minute == undefined ||
      isNaN(minute)
    ) {
      if (value !== undefined) onChange(null);
      return;
    }

    const time = militaryTime
        ? parse(`${hour}:${minute}`, "H:m", new Date())
        : parse(`${hour}:${minute} ${amPm || "am"}`, "h:m aaa", new Date()),
      newValue = format(time, "H:mm");
    if (newValue != value) onChange(newValue);
  }, [hour, minute, amPm]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={`flex flex-none ${
        classes?.container ? classes.container : ""
      }`}
      onBlur={onBlur}
    >
      {!hideIcon && (
        <div
          className={`pl-3 pr-0 py-2 hidden xs:block ${className} ${
            classes?.icon ? classes.icon : ""
          } rounded rounded-r-none`}
        >
          <Icon
            type="clock"
            muted
            className="h-5 w-5 mr-3 my-1"
            aria-hidden="true"
          />
        </div>
      )}

      {loading ? (
        <div className={`${className} text-center w-24`} />
      ) : (
        <>
          <HourSelector
            className={`font-number pl-0 pr-1 py-2 ${className} ${
              classes?.hour ? classes.hour : ""
            } ${!hideIcon ? "rounded rounded-r-none xs:rounded-none" : ""}`}
            disabled={disabled}
            militaryTime={militaryTime}
            onChange={(value) => setHour(value)}
            value={hour}
          />

          <div
            className={`pl-1 pr-1 pt-2 ${className} ${
              classes?.colon ? classes.colon : ""
            }`}
          >
            :
          </div>

          <MinuteSelector
            className={`font-number pl-1 ${
              militaryTime ? "pr-3" : "pr-2"
            } py-2 ${className} ${classes?.minute ? classes.minute : ""} ${
              !militaryTime ? "rounded-none" : "rounded rounded-l-none"
            }`}
            disabled={disabled}
            onChange={(value) => setMinute(value)}
            value={minute}
          />

          {!militaryTime && (
            <AmPmSelector
              className={`pl-1 pr-3 ${className} ${
                classes?.ampm ? classes.ampm : ""
              } rounded rounded-l-none`}
              disabled={disabled}
              onChange={(value) => setAmPm(value)}
              value={amPm}
            />
          )}
        </>
      )}
    </div>
  );
}
